/** Claude's own vision downscale threshold; larger is upload cost for no detail. */
export const MAX_EDGE = 1568;

/** Hard stop before sending. A resized phone photo is ~400KB of base64. */
export const MAX_BASE64_BYTES = 1_500_000;

export interface PreparedImage {
  mediaType: 'image/jpeg';
  data: string;
  /** For telling the person what was actually sent. */
  bytes: number;
}

/**
 * Shrinks a photo in the browser before it is sent.
 *
 * A phone photo is 3–6MB, and base64 adds a third — enough to be slow and, past
 * a point, to be refused. Resizing to the threshold the model downscales to
 * anyway costs nothing in accuracy and turns the upload into about 400KB.
 *
 * Quality steps down rather than failing outright, because a slightly softer
 * photo still reads and a rejected one does not.
 */
export async function prepareImage(file: File): Promise<PreparedImage> {
  if (!file.type.startsWith('image/')) {
    throw new Error('not-an-image');
  }

  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no-canvas');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  for (const quality of [0.85, 0.7, 0.6]) {
    const data = canvas.toDataURL('image/jpeg', quality).split(',')[1] ?? '';
    if (data.length <= MAX_BASE64_BYTES) {
      return { mediaType: 'image/jpeg', data, bytes: data.length };
    }
  }

  throw new Error('too-large');
}

/** `%PDF`, the four bytes every PDF starts with. */
const startsWithPdfMagic = (bytes: Uint8Array): boolean =>
  bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;

/**
 * Whether to treat a chosen file as a PDF.
 *
 * The MIME type the file picker reports cannot be relied on: Android's picker
 * and files coming from Drive often hand over an empty `type`, and sending one
 * of those down the image path fails with nothing useful to say. The name is the
 * fallback, and `preparePdf` confirms it against the file's first bytes once it
 * has them.
 */
export const looksLikePdf = (file: File): boolean =>
  file.type === 'application/pdf' || (file.type === '' && /\.pdf$/i.test(file.name));

/** A PDF is sent whole; base64 adds a third, and the API refuses past 6MB. */
export const MAX_PDF_BYTES = 4_500_000;

/**
 * Reads a venue's PDF for sending.
 *
 * Nothing is resized or re-encoded: a PDF carries its own text layer, and the
 * model reads that rather than pixels, so shrinking it would only lose the thing
 * that makes it better than a screenshot. The size cap is therefore a real
 * limit rather than a target to compress towards — and it is checked here, in
 * the browser, so an oversized brochure says so instantly instead of after a
 * long upload and a gateway error with no message.
 */
export async function preparePdf(file: File): Promise<{ data: string; bytes: number }> {
  if (file.size > MAX_PDF_BYTES) throw new Error('too-large');

  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  // The file's own first bytes, not the type the picker claimed — see
  // `looksLikePdf`. By here the file is read anyway, so this is the free and
  // truthful check.
  if (!startsWithPdfMagic(bytes)) throw new Error('not-a-pdf');
  // In chunks: String.fromCharCode spread over a multi-megabyte array blows the
  // call stack, which presents as a blank failure on exactly the large files
  // this is for.
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  const data = btoa(binary);
  return { data, bytes: data.length };
}
